import React, { useState, useRef, useEffect } from 'react';
import { DateTime } from 'luxon';
import { compressImages, canCompressImage } from '@/utils/imageCompression';
import { useHumanHandoverStore } from '@/stores/humanHandover/humanHandoverStore';
import { ACCEPTED_IMAGE_TYPES } from '@/constants';
import useFileUploadPermission from '@/hooks/useFileUploadPermission';
import { saveChatbotImages } from '@/services/customization.service';
import { uploadFileToConversation } from '@/services/humanHandover';
import isValidFileType from '@/helpers/validateFileType';
import DLoading from '@/components/DLoading';
import { useSoundEffects } from '@/hooks/useSoundEffects';
import { useHorizontalDragHoverScroll } from '../../../hooks/useHorizontalDragHoverScroll';
import MessageInput from '../MessageInput';
import ChatMessage from '../ChatMessage';
import SuggestedPrompts, { SuggestedPromptsContainer } from '../SuggestedPrompts';
import UserActionButton from '../UserActionButton';
import UserIcon from '../Icons/UserIcon';
import ConsentButtons from '../ConsentButtons';
import ResolveButtons from '../ResolveButtons';
import EnableFileButton from '../EnableFileButton';
import generateMarkdownBubble from '@/helpers/generateBubbleMessages';
import transformLinkUri from '@/helpers/transformLinkUri';
import CalendarIcon from '../Icons/CalendarIcon';
import UploadFile from '../UploadFile';
import DropUpload from '../DropUpload';
import TypingIndicator from '@/components/Global/TypingIndicator';
import './index.css';
import DLoaderBubbleLoading from '@/components/Global/DLoaderBubbleLoading';
import LLMSelector from '@/components/LLMSelector';
import SourcesPopup from '@/components/SourcesPopup';

const Chat = ({ 
  config,
  chatContainerRef,
  isInApp,
  isPreviewMode,
  showInAppHeader = false,
  isDanteFaq = false,
  handleSendQuestion,
  hiddenConversation = false,
  hiddenPoweredByDante = false,
  handleCloseButton = () => {},
  handleOpenDanteConversations = () => {},
  handleOpenDanteFaq = () => {},
  handleOpenLiveAgent = () => {},
  showCloseBtn = false,
  showMenuBtn = false,
  isAnswerLoading = false,
  showButtons = true,
  dynamicButtons,
  hideFooter = false,
  handleFooterButton,
  showConsent = false,
  setShowConsent = () => {},
  hasMic = false,
  showSuggestionPrompts = false,
  isLoadingSuggestions = false,
  sources,
  openSources,
  setOpenSources,
  showSources,
  sourcesLoading,
  chatImageLoader,
  isInHumanHandoverApp = false,
  humanHandoverConfig = {},
  humanHandoverMarkResolved = () => {},
  humanHandoverTakeConversation = () => {},
  setQuestionIsQuickResponse = () => {},
  interactingWithLiveAgent = false,
  isLiveAgentResolved = false,
  pollResponse = false,
  messagesLoading = false,
  handleShowVoice = () => { },
  isPreviousConversationHasLiveAgent = false,
  showEmailTranscript = false,
  setShowEmailTranscript = () => {},
  currectConversationId = null,
  liveAgentConversationId = null,
  setNotification = () => {},
  isTyping = false,
  typingUser = null,
  onTypingStart = () => {},
  onTypingStop = () => {},
  leadsGenMode = false,
  leadGenInputs = {},
  place = '',
  // New UI props
  messages = [],
  onSendMessage,
  isLoading = false,
  className = '',
  suggestedPrompts = [],
  suggestedPromptsConfig,
  onPromptClick,
  handleMessagesOnChat = () => {},
  ...props
}) => {
  const { openedConversation } = useHumanHandoverStore((state) => state);
  const [showConversation, setShowConversation] = useState(!hiddenConversation);
  const [question, setQuestion] = useState('');
  const [isMicrophoneOpen, setIsMicrophoneOpen] = useState(hasMic);
  const [isRecording, setIsRecording] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(!config?.public || false);

  // Check if file upload is allowed based on context and settings
  const isFileUploadAllowed = useFileUploadPermission(
    isInHumanHandoverApp,
    pollResponse,
    config?.token,
    liveAgentConversationId || currectConversationId
  );

  const [uploadedImages, setUploadedImages] = useState([]);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [isDragActive, setIsDragActive] = useState(false);
  const [draggedFiles, setDraggedFiles] = useState([]);
  const [isUploadingFiles, setIsUploadingFiles] = useState(false);
  const [isTextareaFocused, setIsTextareaFocused] = useState(false);
  const [hasInteracted, setHasInteracted] = useState(false);
  const [isTextareaScrolling, setIsTextareaScrolling] = useState(false);
  const fileInputRef = useRef(null);
  const textareaRef = useRef(null);
  const [showMarkResolvedButton, setShowMarkResolvedButton] = useState(false);
  const [showEnableFileButton, setShowEnableFileButton] = useState(false);
  const [shouldShowSuggestedPrompts, setShouldShowSuggestedPrompts] = useState(true);

  // Typing indicator refs and state
  const typingTimeoutRef = useRef(null);
  
  // Initialize sound effects with customization data
  const { playMessageSendSound } = useSoundEffects({ customizationData: config });
  const typingSentRef = useRef(false);
  const lastTypingTimeRef = useRef(0);

  const messagesEndRef = useRef(null);
  const typingIndicatorRef = useRef(null);
  const [inputValue, setInputValue] = useState('');
  const chatWrapperRef = useRef(null);
  const chatMessagesContainerRef = useRef(null);
  const inputContainerRef = useRef(null);
  const [overlayBottom, setOverlayBottom] = useState(0);
  const [assistantMinHeight, setAssistantMinHeight] = useState(null);
  
  // Refs for action row scrolling
  const underLastMessageActionRowRef = useRef(null);
  const bottomActionRowRef = useRef(null);
  
  // Hooks for horizontal drag hover scroll
  const { scrollToEnd: scrollUnderLastMessageToEnd } = useHorizontalDragHoverScroll(underLastMessageActionRowRef, {
    enableDragScroll: true,
    enableHoverScroll: true, // Keep enabled for manual triggering
    dragSpeed: 2,
  });

  const { scrollToEnd: scrollBottomToEnd } = useHorizontalDragHoverScroll(bottomActionRowRef, {
    enableDragScroll: true,
    enableHoverScroll: true, // Keep enabled for manual triggering
    dragSpeed: 2,
  });



  // Cleanup typing state on unmount
  useEffect(() => {
    return () => {
      if (typingTimeoutRef.current && onTypingStop && (isInHumanHandoverApp || pollResponse)) {
        onTypingStop();
        typingTimeoutRef.current = null;
      }
    };
  }, [isInHumanHandoverApp, pollResponse, onTypingStop]);

  // Stop typing when conversation is resolved
  useEffect(() => {
    if (isLiveAgentResolved && (isInHumanHandoverApp || pollResponse) && onTypingStop) {
      onTypingStop();

      // Clear typing timer
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
        typingTimeoutRef.current = null;
      }

      // Reset typing state
      typingSentRef.current = false;
    }
  }, [isLiveAgentResolved, isInHumanHandoverApp, pollResponse, onTypingStop]);

  // Check localStorage for kb_id specific visibility of suggested prompts
  useEffect(() => {
    if (suggestedPromptsConfig?.showOncePerKbId && suggestedPromptsConfig?.kb_id) {
      const storageKey = `suggested_prompts_shown_${suggestedPromptsConfig.kb_id}`;
      const hasShown = localStorage.getItem(storageKey);
      setShouldShowSuggestedPrompts(!hasShown);
    } else {
      setShouldShowSuggestedPrompts(true);
    }
  }, [suggestedPromptsConfig?.kb_id, suggestedPromptsConfig?.showOncePerKbId]);

  // Scroll to bottom when suggested prompts appear under the last message
  useEffect(() => {
    if (
      suggestedPromptsConfig?.position === 'underLastMessage' &&
      suggestedPromptsConfig?.showSuggestionPrompts &&
      shouldShowSuggestedPrompts &&
      suggestedPrompts?.length > 0 &&
      messages.filter(message => message.type !== 'welcome_message').length > 0 
    ) {
      setTimeout(() => {
        scrollToBottom();
      }, 100);
    }
  }, [suggestedPrompts, shouldShowSuggestedPrompts, messages.length, suggestedPromptsConfig?.position, suggestedPromptsConfig?.showSuggestionPrompts]);

  // Auto-focus only on initial load, not on mobile to prevent cursor jumping
  // Also disable auto-focus when embedded (isInApp=false) to prevent parent page scroll
  useEffect(() => {
    const isMobile = window.innerWidth <= 768 || 'ontouchstart' in window;
    const isEmbedded = !isInApp;

    if (!isAnswerLoading && textareaRef.current && !hasInteracted && !isMobile && !isEmbedded) {
      if (document.activeElement !== textareaRef.current) {
        textareaRef.current.focus({ preventScroll: true });
      }
    }
  }, [isAnswerLoading, hasInteracted, isInApp]);

  // Auto-resize textarea - simplified for mobile stability
  useEffect(() => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    const isMobile = window.innerWidth <= 768 || 'ontouchstart' in window;

    // Store cursor position to prevent jumping
    const cursorStart = textarea.selectionStart;
    const cursorEnd = textarea.selectionEnd;

    // Temporarily disable transition for measurement
    textarea.style.transition = 'none';
    textarea.style.height = 'auto';

    const computedStyle = window.getComputedStyle(textarea);
    let lineHeight = parseFloat(computedStyle.lineHeight);
    if (isNaN(lineHeight)) {
      lineHeight = parseFloat(computedStyle.fontSize) * 1.2;
    }

    const lines = Math.ceil(textarea.scrollHeight / lineHeight);
    const maxHeight = lineHeight * 4;
    let newHeight = lines >= 2 ? Math.min(textarea.scrollHeight, maxHeight) : lineHeight;

    // Handle scrolling
    if (textarea.scrollHeight > maxHeight) {
      textarea.style.overflowY = 'auto';
      setIsTextareaScrolling(true);
    } else {
      textarea.style.overflowY = 'hidden';
      setIsTextareaScrolling(false);
    }

    // Apply new height with appropriate transition
    requestAnimationFrame(() => {
      const duration = isMobile ? '0.1s' : '0.25s';
      textarea.style.transition = `height ${duration} ease-out`;
      textarea.style.height = `${newHeight}px`;

      // Restore cursor position if textarea is focused
      if (document.activeElement === textarea) {
        textarea.setSelectionRange(cursorStart, cursorEnd);
      }
    });
  }, [question]);

  useEffect(() => {
    setShowConversation(!hiddenConversation);
  }, [hiddenConversation]);

  useEffect(() => {
    setIsMicrophoneOpen(hasMic);
  }, [hasMic]);

  useEffect(() => {
    setIsMicrophoneOpen(false);
  }, [config?.show_microphone]);

  // Track user interaction and prevent focus issues on mobile
  useEffect(() => {
    const handleManualInteraction = (e) => {
      if (e.target !== textareaRef.current) {
        setHasInteracted(true);
      }
    };

    // Prevent hidden elements from stealing focus on mobile
    const handleFocusChange = (e) => {
      const isMobile = window.innerWidth <= 768 || 'ontouchstart' in window;
      if (isMobile && e.target && e.target.type === 'file') {
        e.preventDefault();
        if (textareaRef.current && isTextareaFocused) {
          textareaRef.current.focus({ preventScroll: true });
        }
      }
    };

    const bubbleElement = document.querySelector('.bubble');
    if (bubbleElement) {
      bubbleElement.addEventListener('click', handleManualInteraction);
      bubbleElement.addEventListener('touchstart', handleManualInteraction);
    }

    document.addEventListener('focusin', handleFocusChange);

    return () => {
      if (bubbleElement) {
        bubbleElement.removeEventListener('click', handleManualInteraction);
        bubbleElement.removeEventListener('touchstart', handleManualInteraction);
      }
      document.removeEventListener('focusin', handleFocusChange);
    };
  }, [isTextareaFocused]);

  // Helper function to determine if message sending should be disabled
  const isMessageSendingDisabled = () => {
    // Standard restrictions
    if (isPreviewMode || isAnswerLoading || isUploadingFiles) {
      return true;
    }

    // Disable if conversation is resolved
    if (isLiveAgentResolved) {
      return true;
    }

    // Human Handover specific restrictions
    if (isInHumanHandoverApp && humanHandoverConfig?.isTaken) {
      // If conversation is taken but user is not assigned to it, disable sending
      return !humanHandoverConfig?.isAssignedToCurrentUser;
    }

    return false;
  };

  const handleSend = async (tempQuestion = '') => {
    // Check if message sending is disabled
    if (isMessageSendingDisabled()) {
      return;
    }

    // Send typing stop event when sending a message
    if ((isInHumanHandoverApp || pollResponse) && onTypingStop && !isLiveAgentResolved) {
      onTypingStop();

      // Clear typing timer
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
        typingTimeoutRef.current = null;
      }

      // Reset typing state
      typingSentRef.current = false;
    }

    if ((question.trim().length > 0 || tempQuestion.trim().length > 0 || uploadedImages.length > 0 || uploadedFiles.length > 0)) {
      setHasInteracted(true);

      // Process images if there are any
      let imageUrls = [];
      if (uploadedImages.length > 0 && config?.kb_id) {
        // Create FormData to upload images
        const formData = new FormData();
        uploadedImages.forEach((image, index) => {
          if (typeof image !== 'string') {
            // Preserve the original filename when appending to FormData
            const filename = image.name || `image-${index + 1}.${image.type?.split('/')[1] || 'jpg'}`;
          
            formData.append('file', image, filename);
          }
        });

        try {

          const response = await saveChatbotImages(config.kb_id, formData);


          if (response.data && response.data.url) {
          
            imageUrls.push(response.data.url);
          }
        } catch (error) {
          console.error('Error uploading images:', error);
        }
      }

      // Process files if there are any
      let fileUrls = [];
      if (uploadedFiles.length > 0 && config?.kb_id) {
        try {

          // Upload each file individually
          for (const file of uploadedFiles) {
            const filename = file.name || `file-${Date.now()}.${file.type?.split('/')[1] || 'txt'}`;
        

            const response = await uploadFileToConversation(config.kb_id, file);
        

            if (response.data && response.data.url) {
           
              fileUrls.push(response.data.url);
            } 
          }

      
        } catch (error) {
          console.error('Error uploading files:', error);
        }
      }

      // Send question with all file URLs (images and documents combined)
      // ChatMessage component will separate them based on file type
      const allFileUrls = [...imageUrls, ...fileUrls];
      handleSendQuestion(tempQuestion || question, allFileUrls);
      isDanteFaq && handleOpenDanteFaq();

      playMessageSendSound();

      setShowConversation(true);
      setQuestion('');
      setUploadedImages([]);
      setUploadedFiles([]);
    }
  };

  const handleSendMessage = async () => {
    if (isMessageSendingDisabled()) {
      return;
    }

    // Send typing stop event when sending a message
    if ((isInHumanHandoverApp || pollResponse) && onTypingStop && !isLiveAgentResolved) {
      onTypingStop();

      // Clear typing timer
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
        typingTimeoutRef.current = null;
      }

      // Reset typing state
      typingSentRef.current = false;
    }

    const hasContent = inputValue.trim().length > 0 || uploadedImages.length > 0 || uploadedFiles.length > 0;

    if (hasContent) {
      // Set uploading state to disable send button
      setIsUploadingFiles(true);

      try {
        // Process images if there are any
        let imageUrls = [];
        if (uploadedImages.length > 0 && config?.kb_id) {
          try {
            // Upload each image individually
            for (const image of uploadedImages) {
              if (typeof image !== 'string') {
                // Create FormData for each image
                const formData = new FormData();
                const filename = image.name || `image-${Date.now()}.${image.type?.split('/')[1] || 'jpg'}`;
                formData.append('file', image, filename);

                const response = await saveChatbotImages(config.kb_id, formData);

                if (response.data && response.data.url) {
                  imageUrls.push(response.data.url);
                }
              }
            }
          } catch (error) {
            console.error('Error uploading images:', error);
          }
        }

        // Process files if there are any
        let fileUrls = [];
        if (uploadedFiles.length > 0 && config?.kb_id) {
          try {
            // Upload each file individually
            for (const file of uploadedFiles) {
              const filename = file.name || `file-${Date.now()}.${file.type?.split('/')[1] || 'txt'}`;

              const response = await uploadFileToConversation(config.kb_id, file);

              if (response.data && response.data.url) {
                fileUrls.push(response.data.url);
              }
            }
          } catch (error) {
            console.error('Error uploading files:', error);
          }
        }

        // Send question with all file URLs (images and documents combined)
        // ChatMessage component will separate them based on file type
        const allFileUrls = [...imageUrls, ...fileUrls];
        handleSendQuestion(inputValue, allFileUrls);
        isDanteFaq && handleOpenDanteFaq();

        playMessageSendSound();

        setShowConversation(true);
        setInputValue('');
        setUploadedImages([]);
        setUploadedFiles([]);
      } finally {
        // Always reset uploading state, even if there's an error
        setIsUploadingFiles(false);
      }
    }
  };

  const handleSuggestionClick = (suggestion) => {
    // Check if message sending is disabled
    if (isMessageSendingDisabled()) {
      return;
    }

    setHasInteracted(true);
    handleSendQuestion(suggestion.content);
    playMessageSendSound();
  };

  const handleOpenMicrophone = () => {
    setIsMicrophoneOpen(true);
  };

  const handleRecordingComplete = (audioBlob) => {
    setIsMicrophoneOpen(false);
  };

  const handleImageUpload = async (filesOrEvent) => {
    // Handle both direct files array and event.target.files format
    let files;
    if (filesOrEvent.target && filesOrEvent.target.files) {
      // It's an event object
      files = filesOrEvent.target.files;
    } else if (Array.isArray(filesOrEvent)) {
      // It's already an array of files
      files = filesOrEvent;
    } else {
      // Unknown format
      console.error('Invalid format for handleImageUpload:', filesOrEvent);
      return;
    }
    
    if (files && files.length > 0) {
      // Filter only accepted image files
      const imageFiles = Array.from(files).filter(file =>
        ACCEPTED_IMAGE_TYPES.includes(file.type)
      );

      if (imageFiles.length > 0) {
        try {
          // Separate compressible and non-compressible files
          const compressibleFiles = imageFiles.filter(canCompressImage);
          const nonCompressibleFiles = imageFiles.filter(file => !canCompressImage(file));

          // Compress the compressible images
          let processedFiles = [...nonCompressibleFiles];
          if (compressibleFiles.length > 0) {
            const compressedFiles = await compressImages(compressibleFiles);
            processedFiles = [...processedFiles, ...compressedFiles];
          }

          setUploadedImages(prev => [...prev, ...processedFiles]);

        } catch (error) {
          console.error('Error processing images in Chat component:', error);
          // Fallback: add original files if compression fails
          setUploadedImages(prev => [...prev, ...imageFiles]);
        }
      }
    }
  };

  const handleRemoveImage = (indexToRemove) => {
    setUploadedImages(prev => prev.filter((_, index) => index !== indexToRemove));
  };

  const handleFileUpload = async (filesOrEvent) => {
    // Handle both direct files array and event.target.files format
    let files;
    if (filesOrEvent.target && filesOrEvent.target.files) {
      // It's an event object
      files = filesOrEvent.target.files;
    } else if (Array.isArray(filesOrEvent)) {
      // It's already an array of files
      files = filesOrEvent;
    } else {
      // Unknown format
      console.error('Invalid format for handleFileUpload:', filesOrEvent);
      return;
    }
    
    if (files && files.length > 0) {
      // Filter only valid file types (excluding images since they're handled separately)
      const validFiles = Array.from(files).filter(file =>
        isValidFileType(file) && !ACCEPTED_IMAGE_TYPES.includes(file.type)
      );

      if (validFiles.length > 0) {
        setUploadedFiles(prev => [...prev, ...validFiles]);
      }
    }
  };

  const handleRemoveFile = (indexToRemove) => {
    setUploadedFiles(prev => prev.filter((_, index) => index !== indexToRemove));
  };

  // Drag and drop handlers for the entire chat area
  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(true);
    
    // Try to get files from dataTransfer items if available
    if (e.dataTransfer.items && e.dataTransfer.items.length > 0) {
      const files = [];
      for (let i = 0; i < e.dataTransfer.items.length; i++) {
        // If the item is a file
        if (e.dataTransfer.items[i].kind === 'file') {
          const file = e.dataTransfer.items[i].getAsFile();
          if (file) {
            files.push(file);
          }
        }
      }
      if (files.length > 0) {
        setDraggedFiles(files);
      }
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(true);
    
    // Some browsers only provide file information on drop, not during dragover
    // But we try to get it if available for better UX
    if (e.dataTransfer.items && e.dataTransfer.items.length > 0 && draggedFiles.length === 0) {
      const files = [];
      for (let i = 0; i < e.dataTransfer.items.length; i++) {
        if (e.dataTransfer.items[i].kind === 'file') {
          const file = e.dataTransfer.items[i].getAsFile();
          if (file) {
            files.push(file);
          }
        }
      }
      if (files.length > 0) {
        setDraggedFiles(files);
      }
    }
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    // Only set dragActive to false if we're leaving the chat area entirely
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setIsDragActive(false);
      setDraggedFiles([]);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(false);
    
    // Clear dragged files state
    setDraggedFiles([]);

    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      const files = Array.from(e.dataTransfer.files);

      // Separate accepted image types from all other files
      const acceptedImageFiles = files.filter(file => ACCEPTED_IMAGE_TYPES.includes(file.type));
      const otherFiles = files.filter(file => !ACCEPTED_IMAGE_TYPES.includes(file.type));

      if (acceptedImageFiles.length > 0) {
        handleImageUpload(acceptedImageFiles);
      }

      if (otherFiles.length > 0) {
        handleFileUpload(otherFiles);
      }
    }
  };



  const isLiveAgentAvailable = () => {
    if (!config?.talk_to_live_agent) {
      return false;
    }

    if (config?.live_agent_always_active) {
      return true;
    }

    const schedule = config?.live_agent_list_of_schedules || [];
    
    try {
      // Get the configured timezone or default to UTC
      const timezone = config?.human_handover_timezone || Intl?.DateTimeFormat()?.resolvedOptions()?.timeZone || 'Europe/Belgrade';
      
      // Get current date/time in the configured timezone using Luxon
      const now = DateTime.now().setZone(timezone);
      const currentDay = now.weekdayLong; // Returns full weekday name
      const currentTime = now.toFormat('HH:mm'); // 24-hour format

      const todaySchedule = schedule.find((s) => s.day === currentDay);

      if (todaySchedule && dynamicButtons?.show_live_agent) {
        // Convert schedule times to DateTime objects in the configured timezone for comparison
        const scheduleStart = DateTime.fromFormat(todaySchedule.from, 'HH:mm', { zone: timezone });
        const scheduleEnd = DateTime.fromFormat(todaySchedule.to, 'HH:mm', { zone: timezone });
        const currentDateTime = DateTime.fromFormat(currentTime, 'HH:mm', { zone: timezone });

        return currentDateTime >= scheduleStart && currentDateTime <= scheduleEnd;
      }
    } catch (error) {
      console.error('Error checking live agent availability:', error);
    }

    return false;
  };

  const handleQuickResponseClick = (quickResponseContent) => {
    setQuestion(quickResponseContent);
    setQuestionIsQuickResponse(true);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handlePromptClick = (prompt, index) => {
    console.log('handlePromptClick', prompt, index);
    // Mark as shown in localStorage when user interacts
    if (suggestedPromptsConfig?.showOncePerKbId && suggestedPromptsConfig?.kb_id) {
      const storageKey = `suggested_prompts_shown_${suggestedPromptsConfig.kb_id}`;
      localStorage.setItem(storageKey, 'true');
      setShouldShowSuggestedPrompts(false);
    }

    if (onPromptClick) {
      // Extract the string content from the prompt (handle both string and object formats)
      const promptContent = typeof prompt === 'string' ? prompt : prompt.content;
      onPromptClick(promptContent, index);
    }
    // Play sound when clicking on suggested prompts
    playMessageSendSound();
  };

  const handleAskHumanClick = () => {
    // Add "Ask a human" message directly to chat without triggering a response
    handleMessagesOnChat('add', {
      id: Date.now().toString(), // Simple ID generation
      content: 'Ask a human',
      role: 'user',
      date_created: new Date().toISOString(),
      type: 'live_agent_message',
    });

    // Then trigger the live agent flow after a short delay
    setTimeout(() => {
      handleOpenLiveAgent();
    }, 100);
  };

  const handleSkipToGeneralChat = () => {
    // Handle skip to general chat logic
    setShowConsent(false);
    handleSend('Skip to general chat');
    // Add any additional logic needed for skipping to general chat
  };

  const handlePersonalize = () => {
    // Handle personalize logic
    setShowConsent(false);
    handleSend('Yes, let\'s personalize');
  };

  const handleFileUploadEnabled = () => {
    // Hide the enable file button after successful enablement
    setShowEnableFileButton(false);
  };


  // Function to scroll to bottom for assistant messages
  const scrollToBottom = () => {
    const chatContainer = document.querySelector('.chat-messages-container');
    if (!chatContainer) return;

    chatContainer.scrollTo({
      top: chatContainer.scrollHeight,
      behavior: 'smooth'
    });
  };

  // Auto-scroll logic for user and assistant messages
  useEffect(() => {
    if (messages.length > 0) {
      const lastMessage = messages[messages.length - 1];

      if(isInHumanHandoverApp) {
        scrollToBottom();
        return;
      }
      
      if (lastMessage.role === 'user') {
        // Scroll user message to top
        setTimeout(() => {
          const chatContainer = document.querySelector('.chat-messages-container');
          if (!chatContainer) return;

          const userMessages = messages.filter(msg => msg.role === 'user');
          if (userMessages.length === 0) return;

          const lastUserMessage = userMessages[userMessages.length - 1];
          const lastUserMessageIndex = messages.findIndex(msg => msg === lastUserMessage);
          
          const messageElements = chatContainer.querySelectorAll('[data-message-index]');
          const targetElement = messageElements[lastUserMessageIndex];
          
          if (targetElement) {
            const elementOffsetTop = targetElement.offsetTop;
            chatContainer.scrollTo({
              top: elementOffsetTop - 50,
              behavior: 'smooth'
            });
          }
        }, 100);
      } else if (lastMessage.role === 'assistant' && lastMessage.type !== 'welcome_message') {
        // Scroll assistant message to bottom
        setTimeout(() => {
          scrollToBottom();
        }, 100);
      }
    }
  }, [messages]);

  useEffect(() => {
      scrollToBottom();
  }, []);

  // Scroll to typing indicator when it appears
  useEffect(() => {
    if (isTyping) {
      setTimeout(() => {
        scrollToBottom();
      }, 100);
    }
  }, [isTyping]);

  useEffect(() => {
    const recalc = () => {
      const wrapperEl = chatWrapperRef.current;
      const chatMessagesContainerEl = chatMessagesContainerRef.current;
      if (!wrapperEl || !chatMessagesContainerEl) return;

      let bottomOffset = 0;
      bottomOffset = suggestedPromptsConfig.position === 'bottom' ? (wrapperEl.clientHeight - chatMessagesContainerEl.clientHeight) : (wrapperEl.clientHeight - chatMessagesContainerEl.clientHeight - 6);
      setOverlayBottom(bottomOffset);
      setAssistantMinHeight((prev) => {
        const next = chatMessagesContainerEl.clientHeight;
        return prev === next ? prev : next;
      });
    };

    const observers = [];
    const observe = (el) => {
      if (!el || typeof ResizeObserver === 'undefined') return;
      const ro = new ResizeObserver(() => recalc());
      ro.observe(el);
      observers.push(ro);
    };

    observe(chatWrapperRef.current);
    observe(bottomActionRowRef.current);
    observe(inputContainerRef.current);
    observe(chatMessagesContainerRef.current);

    window.addEventListener('resize', recalc);
    recalc();

    return () => {
      observers.forEach((o) => o.disconnect());
      window.removeEventListener('resize', recalc);
    };
  }, [isAnswerLoading]);

  return (
      <div
        ref={chatWrapperRef}
        className="relative flex flex-col h-full w-full z-0 gap-v3-sm chat-wrapper min-h-0"
        style={{ 
          backgroundColor: config?.surface_color || '#ffffff',
          '--gradient-surface-color': config?.surface_color || '#ffffff',
          '--gradient-bottom': `${overlayBottom}px`
        }}
        onDragEnter={handleDragEnter}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
      <div 
        className="relative pt-v3-sm flex-1 px-v3-sm z-10 overflow-y-auto no-scrollbar h-0 chat-messages-container"
        style={{ backgroundColor: config?.surface_color || '#ffffff' }}
        ref={chatMessagesContainerRef}
      >
        <div className="min-h-full flex flex-col">
        {messagesLoading ? (
          <div className="flex flex-col items-center justify-center grow">
            <DLoaderBubbleLoading show={true} style={{ height: '100%', width: '100%' }} height='h-full'/>
          </div>
        ) : (
          <>
            {/* Show prompts/buttons when no messages and position is underLastMessage */}
            {messages.length === 0 &&
             !isAnswerLoading &&
             suggestedPromptsConfig?.position === 'underLastMessage' &&
             !isInHumanHandoverApp &&
             !(leadsGenMode && (showConsent || leadGenInputs?.question)) && (
              <div className="flex flex-wrap gap-v3-xs pb-2 min-w-0 max-w-full animate-fadeInUp justify-end">
                {dynamicButtons?.show_live_agent && isLiveAgentAvailable() && !interactingWithLiveAgent && !pollResponse && !isLiveAgentResolved && !(leadsGenMode && (showConsent || leadGenInputs?.question)) && (
                  <UserActionButton
                    variant="outline"
                    label="Ask a human"
                    onClick={handleAskHumanClick}
                    showIcon={true}
                    icon={UserIcon}
                    disabled={isPreviewMode}
                    config={config}
                  />
                )}
                {dynamicButtons?.show_calendly_url && !interactingWithLiveAgent && !pollResponse && (
                  <UserActionButton
                    variant="outline"
                    label={config?.calendly_btn_text || 'Book a demo'}
                    onClick={() => window.open(config?.calendly_url, '_blank', 'noopener,noreferrer')}
                    showIcon={true}
                    icon={CalendarIcon}
                    disabled={isPreviewMode}
                  />
                )}
                {suggestedPromptsConfig?.showSuggestionPrompts &&
                 shouldShowSuggestedPrompts &&
                 suggestedPrompts?.length > 0 &&
                 suggestedPrompts.map((prompt, index) => (
                  <SuggestedPrompts
                    key={index}
                    label={typeof prompt === 'string' ? prompt : prompt.content}
                    onClick={() => handlePromptClick(prompt, index)}
                    disabled={isPreviewMode}
                    animationDelay={index * 100}
                  />
                ))}
              </div>
            )}

            {messages.map((message, index) => {
              
              return (
                <div
                  key={index}
                  className="bubble-message-wrapper"
                  data-message-index={index}
                  style={
                    message.role === 'assistant' &&
                    index === messages.length - 1 &&
                    !isInHumanHandoverApp &&
                    !isPreviewMode
                      ? { minHeight: assistantMinHeight ? `${assistantMinHeight - 66}px` : undefined }
                      : {}
                  }
                >
                  <ChatMessage
                    role={message.role}
                    message={message.content !== '' ?  generateMarkdownBubble(transformLinkUri, message, config?.kb_id) : ''}
                    isFirstMessage={index === 0}
                    isLastMessage={index === messages.length - 1}
                    timestamp={message.date_created || message.timestamp}
                    leadsGenMode={leadsGenMode}
                    status={message.status || 'complete'}
                    type={message.type || 'normal'}
                    agent={{
                      agent_name: message.agent_name,
                      agent_title: message.agent_title,
                      agent_role: message.agent_role,
                      agent_profile_pic: message.agent_profile_pic,
                    }}
                    images={message.images || []}
                    files={message.files || []}
                    isFadingOut={message.isFadingOut || false}
                    isFadingIn={message.isFadingIn || false}
                    responseBackground={config?.response_background_color}
                    handleFooterButton={handleFooterButton}
                    messageId={message.id || message.id_temp}
                    reaction={message.reaction || ''}
                    readonly={false}
                    messageIndex={index}
                    hideFooter={hideFooter}
                    isPreviewMode={isPreviewMode}
                    isInHumanHandoverApp={isInHumanHandoverApp}
                    interactingWithLiveAgent={interactingWithLiveAgent}
                    pollResponse={pollResponse}
                    isLastAssistantMessage={(() => {
                      // Find the last assistant message in the conversation
                      const lastAssistantIndex = messages.map((msg, idx) => ({ msg, idx }))
                        .filter(({ msg }) => msg.role !== 'user')
                        .pop()?.idx;
                      return lastAssistantIndex === index;
                    })()}
                    config={config}
                    onFileClick={(fileUrl, fileType) => {
                      // Handle file click - open in new tab or download
                      if (fileType === 'image') {
                        // For images, open in new tab
                        window.open(fileUrl, '_blank');
                      } else {
                        // For files, trigger download
                        const link = document.createElement('a');
                        link.href = fileUrl;
                        link.download = '';
                        link.click();
                      }
                    }}
                    isLastWelcomeMessage={
                      message.role === 'assistant' && 
                      message.type === 'welcome_message' && 
                      (index === messages.length - 1 || messages[index + 1]?.type !== 'welcome_message')
                    }
                    isInApp={isInApp}
                    showSources={showSources}
                    sourcesLoading={sourcesLoading}
                    place={place}
                  />
                  
                  {/* Suggested prompts under the last assistant message */}
                  {index === messages.length - 1 && 
                   message.role === 'assistant' &&
                   !isAnswerLoading &&
                   suggestedPromptsConfig?.position === 'underLastMessage' &&
                   !isInHumanHandoverApp && !(leadsGenMode && (showConsent || leadGenInputs?.question)) && (
                    <div
                      ref={underLastMessageActionRowRef}
                      className="flex flex-wrap gap-v3-xs pb-2 min-w-0 max-w-full animate-fadeInUp justify-end"
                    >
                      {dynamicButtons?.show_live_agent && isLiveAgentAvailable() && !interactingWithLiveAgent && !pollResponse &&  !isLiveAgentResolved && !isInApp && !(leadsGenMode && (showConsent || leadGenInputs?.question)) && (
                        <UserActionButton
                          variant="outline"
                          label="Ask a human"
                          onClick={handleAskHumanClick}
                          showIcon={true}
                          icon={UserIcon}
                          disabled={isPreviewMode}
                          config={config}
                        />
                      )}
                      {dynamicButtons?.show_calendly_url && !interactingWithLiveAgent && !pollResponse && (
                        <UserActionButton
                          variant="outline"
                          label={config?.calendly_btn_text || 'Book a demo'}
                          onClick={() => window.open(config?.calendly_url, '_blank', 'noopener,noreferrer')}
                          showIcon={true}
                          icon={CalendarIcon}
                          disabled={isPreviewMode}
                        />
                      )}
                      {suggestedPromptsConfig?.showSuggestionPrompts && 
                       shouldShowSuggestedPrompts && 
                       suggestedPrompts?.length > 0 && 
                       suggestedPrompts.map((prompt, index) => (
                        <SuggestedPrompts
                          key={index}
                          label={typeof prompt === 'string' ? prompt : prompt.content}
                          onClick={() => handlePromptClick(prompt, index)}
                          disabled={isPreviewMode}
                          animationDelay={index * 100}
                        />
                      ))}
                    </div>
                  )}

                  {index === messages.length - 1 &&
                    messages.length > 0 &&
                    leadsGenMode &&
                    leadGenInputs?.input_type === 'select' &&
                    leadGenInputs?.input_options &&
                    leadGenInputs?.show_options !== false && (
                      <div className='flex gap-v3-xs max-w-[90%] flex-wrap'>
                        {leadGenInputs?.input_options?.map((option, index) => (
                          <div key={index}>
                            <UserActionButton
                              variant="outline"
                              label={option}
                              onClick={() => handleSendQuestion(option)}
                              showIcon={false}
                              disabled={isPreviewMode}
                            />
                          </div>
                        ))}
                      </div>
                    )}
                </div>
              );
            })}

            {/* Typing indicator for human handover */}
            {(isInHumanHandoverApp || pollResponse) && isTyping && typingUser && (
              <div ref={typingIndicatorRef} className="flex items-start gap-v3-xs mb-v3-sm animate-fadeIn">
                <div className="flex items-center gap-v3-xs">
                  {typingUser.profile_pic && (
                    <img
                      src={typingUser.profile_pic}
                      alt={typingUser.name}
                      className="w-8 h-8 rounded-full object-cover"
                    />
                  )}
                  <div className="flex flex-col">
                    <TypingIndicator show={true} dotsOnly={true} />
                  </div>
                </div>
              </div>
            )}
          </>
        )}
          <div ref={messagesEndRef} />
 
        </div>

        </div>
        {(() => {
          // Check if any of the bottom action elements should be shown
          const showLiveAgent = dynamicButtons?.show_live_agent && isLiveAgentAvailable() && !isAnswerLoading && !interactingWithLiveAgent && !pollResponse && !isInApp && !(leadsGenMode && (showConsent || leadGenInputs?.question));
          const showCalendly = dynamicButtons?.show_calendly_url && !interactingWithLiveAgent && !pollResponse && !isAnswerLoading;
          const showSuggestedPrompts = suggestedPromptsConfig?.showSuggestionPrompts && !(leadsGenMode && (showConsent || leadGenInputs?.question));

          const hasAnyActiveElement = showLiveAgent || showCalendly || showSuggestedPrompts;

          return suggestedPromptsConfig?.position === 'bottom' && !isInHumanHandoverApp && hasAnyActiveElement ;
        })() && (
          <div
          ref={bottomActionRowRef}
          className="flex gap-v3-xs pb-1 items-center px-v3-sm animate-fadeInUp overflow-x-auto overflow-y-hidden scrollbar-hide cursor-grab select-none"
          style={{ scrollbarWidth: 'none', msOverflowStyle: 'none', WebkitOverflowScrolling: 'touch' }}

        >
          {dynamicButtons?.show_live_agent && isLiveAgentAvailable() && !isAnswerLoading  && !interactingWithLiveAgent && !pollResponse &&  !isLiveAgentResolved && !(leadsGenMode && (showConsent || leadGenInputs?.question)) && (
            <div className="flex-shrink-0">
              <UserActionButton
                variant="outline"
                label="Ask a human"
                onClick={handleAskHumanClick}
                showIcon={true}
                icon={UserIcon}
                disabled={isPreviewMode}
                config={config}
              />
            </div>
          )}
          {dynamicButtons?.show_calendly_url && !interactingWithLiveAgent && !pollResponse && !isAnswerLoading && (
            <div className="flex-shrink-0">
              <UserActionButton
                variant="outline"
                label={config?.calendly_btn_text || "Book a demo"}
                onClick={() => window.open(config?.calendly_url, '_blank')}
                showIcon={true}
                icon={CalendarIcon}
                disabled={isPreviewMode}
              />
            </div>
          )}
          {(() => {
            const shouldShow = suggestedPromptsConfig?.showSuggestionPrompts;
            return shouldShow;
          })() &&
          <SuggestedPromptsContainer
            className="flex-1 min-w-0"
            prompts={suggestedPrompts}
            onPromptClick={handlePromptClick}
            kb_id={suggestedPromptsConfig.kb_id}
            showOncePerKbId={suggestedPromptsConfig.showOncePerKbId}
            disabled={isPreviewMode}
            enableDragScroll={false}
            enableHoverScroll={true}        // enable hover detection within prompts area
            useParentScroller={true}
            onLastPromptHover={scrollBottomToEnd} // << key: hover callback
          />}
        </div>
      )}

        {/* Message Input or Consent Buttons */}
        <div ref={inputContainerRef} className={`px-v3-sm pb-v3-sm w-full ${!config?.remove_watermark && !isInHumanHandoverApp ? '!pb-0' : ''}`}>
          {showConsent && !isInHumanHandoverApp ? (
            <ConsentButtons
              onSkipToGeneralChat={() => {
                handleSkipToGeneralChat();
                playMessageSendSound();
              }}
              onPersonalize={() => {
                handlePersonalize();
                playMessageSendSound();
              }}
            />
          ) : showMarkResolvedButton && humanHandoverConfig?.isTaken && humanHandoverConfig?.isAssignedToCurrentUser && !isLiveAgentResolved? (
            <ResolveButtons
              onCancel={() => setShowMarkResolvedButton(false)}
              onMarkResolved={humanHandoverMarkResolved}
            />
          ) : showEnableFileButton && isInHumanHandoverApp && humanHandoverConfig?.isTaken && humanHandoverConfig?.isAssignedToCurrentUser && !isFileUploadAllowed && !isLiveAgentResolved ? (
            <EnableFileButton
              onCancel={() => setShowEnableFileButton(false)}
              onEnableFile={handleFileUploadEnabled}
              conversationId={liveAgentConversationId}
            />
          ) : isLiveAgentResolved && isInHumanHandoverApp ? (
            <div className="flex justify-center items-center">
              <p className="text-sm font-medium leading-[160%] text-v3-purple-primary">Agent resolved this</p>
            </div>
           ) : isInHumanHandoverApp && !humanHandoverConfig?.isTaken ? (
             <div className="flex justify-center items-center">
              <button className="flex h-10 px-6 py-[10px] flex-col justify-center items-center flex-1 rounded-v3-md bg-v3-purple-primary transition-colors duration-200" onClick={humanHandoverTakeConversation}>
               <span className="text-center text-white font-inter-variable text-sm font-medium leading-[160%]">Join</span>
              </button>
             </div>
           )
          :(
        <>
        
        {/* Conditional rendering: DropUpload when dragging, MessageInput otherwise */}
        {isDragActive ? (
          <DropUpload draggedFiles={draggedFiles} />
        ) : (
          <MessageInput
            value={inputValue}
            onChange={(e) => {
              const newValue = e.target.value;
              setInputValue(newValue);

              // Send typing events for human handover (debounced to once every 7 seconds)
              // Send typing events when in human handover app OR when pollResponse is active (user side)
              if ((isInHumanHandoverApp || pollResponse) && onTypingStart && onTypingStop && !isLiveAgentResolved) {
                if (newValue.trim() !== '') {
                  // Start typing when there's content
                  const now = Date.now();

                  // Only send typing start if we haven't sent one recently
                  if (!typingSentRef.current || now - lastTypingTimeRef.current > 7000) {
                    onTypingStart();
                    typingSentRef.current = true;
                    lastTypingTimeRef.current = now;
                  }

                  // Clear any existing timer
                  if (typingTimeoutRef.current) {
                    clearTimeout(typingTimeoutRef.current);
                  }

                  // Set timer to reset typing state after 7 seconds of no typing
                  typingTimeoutRef.current = setTimeout(() => {
                    if (typingSentRef.current) {
                      onTypingStop();
                      typingSentRef.current = false;
                    }
                  }, 7000);
                }
              }
            }}
            onSend={() => handleSendMessage()}
            onKeyPress={handleKeyPress}
            onAIVoiceClick={handleShowVoice}
            placeholder={config?.input_placeholder_text || "Message"}
            disabled={isPreviewMode || isUploadingFiles}
            isLoading={isLoading}
            config={config}
            isPreviewMode={isPreviewMode}
            isInHumanHandoverApp={isInHumanHandoverApp}
            pollResponse={pollResponse}
            onImageUpload={handleImageUpload}
            onFileUpload={handleFileUpload}
            uploadedFiles={uploadedFiles}
            uploadedImages={uploadedImages}
            onRemoveFile={handleRemoveFile}
            onRemoveImage={handleRemoveImage}
            onMarkResolvedClick={() => setShowMarkResolvedButton(true)}
            onEnableFileClick={() => setShowEnableFileButton(true)}
          />
        )}

        {isInApp && !isPreviewMode && !isInHumanHandoverApp && (<div className='pt-1'>
          <LLMSelector />
        </div>)}
      </>
    )}
    </div>

    <SourcesPopup
      open={openSources}
      onClose={() => setOpenSources(false)}
      data={sources}
    />
    </div>
  );
};

export default Chat;
